import { FeatureGateAudience } from '../../__generated__/globalTypes';

import { ruleToFormState } from './ruleSerialization';

import type { FeatureGateRuleNode } from './ruleTree';

/**
 * Computes the row-level diff shown in the edit-save confirmation
 * dialog. The dialog needs the smallest possible signal — just the
 * fields that actually changed — so the operator can scan it in one
 * read before flipping the trigger.
 *
 * Pure value-only logic so the comparison rules stay testable in
 * isolation. The dialog is responsible for layout / styling; this
 * helper only emits the row data.
 *
 * Lookups by rule shape go through `ruleToFormState`, which is
 * order-tolerant on the OR composite — re-saving an unchanged composite
 * after the form serialized it in a different child order will not show
 * a phantom rule diff.
 */

export interface IGateSnapshot {
  isEnabled: boolean;
  ruleTree: FeatureGateRuleNode;
  audience: FeatureGateAudience;
  defaultWhenError: boolean;
  description: string;
}

export interface IDiffRow {
  /** Stable field key — used as a React key. */
  field: keyof IGateSnapshot;
  /** Human-readable field label rendered as the row's left column. */
  label: string;
  /** String form of the previous value. Empty string is rendered as `—`. */
  before: string;
  /** String form of the new value. */
  after: string;
}

/**
 * Returns the changed-field rows between two gate snapshots, in field
 * order. An empty list means the form is byte-for-byte unchanged — the
 * caller should disable the save button in that case rather than
 * opening an empty dialog.
 *
 * Comparisons are value-equal (no object identity assumptions) so a
 * gate that round-trips through the form (rule tree → form state →
 * rule tree) does not emit a phantom diff just because the JS object
 * reference changed.
 */
export function diffGate(prev: IGateSnapshot, next: IGateSnapshot, userEmailById?: Map<string, string>): IDiffRow[] {
  const rows: IDiffRow[] = [];

  if (prev.isEnabled !== next.isEnabled) {
    rows.push({
      field: 'isEnabled',
      label: 'Enabled',
      before: prev.isEnabled ? 'On' : 'Off',
      after: next.isEnabled ? 'On' : 'Off',
    });
  }

  const prevForm = ruleToFormState(prev.ruleTree);
  const nextForm = ruleToFormState(next.ruleTree);
  if (prevForm.adminPct !== nextForm.adminPct) {
    rows.push({
      field: 'ruleTree',
      label: 'Admins rollout %',
      before: `${prevForm.adminPct}`,
      after: `${nextForm.adminPct}`,
    });
  }
  if (prevForm.everyonePct !== nextForm.everyonePct) {
    rows.push({
      field: 'ruleTree',
      label: 'Everyone rollout %',
      before: `${prevForm.everyonePct}`,
      after: `${nextForm.everyonePct}`,
    });
  }

  // Set-equality (order-insensitive) so re-saving the same list in a
  // different order is not a phantom diff — and, crucially, so a 1-for-1
  // user swap (same count, different ids) still surfaces a row. Without
  // these the edit-save path sees `rows.length === 0` and swallows a
  // list-only change with a false "No changes to save."
  if (!sameIdSet(prevForm.allowUserIds, nextForm.allowUserIds)) {
    rows.push({
      field: 'ruleTree',
      label: 'Allow-list users',
      before: formatUserList(prevForm.allowUserIds, userEmailById),
      after: formatUserList(nextForm.allowUserIds, userEmailById),
    });
  }
  if (!sameIdSet(prevForm.denyUserIds, nextForm.denyUserIds)) {
    rows.push({
      field: 'ruleTree',
      label: 'Deny-list users',
      before: formatUserList(prevForm.denyUserIds, userEmailById),
      after: formatUserList(nextForm.denyUserIds, userEmailById),
    });
  }

  if (prev.audience !== next.audience) {
    rows.push({
      field: 'audience',
      label: 'Audience',
      before: AUDIENCE_LABEL[prev.audience],
      after: AUDIENCE_LABEL[next.audience],
    });
  }

  if (prev.defaultWhenError !== next.defaultWhenError) {
    rows.push({
      field: 'defaultWhenError',
      label: 'Default on evaluation failure',
      before: prev.defaultWhenError ? 'On' : 'Off',
      after: next.defaultWhenError ? 'On' : 'Off',
    });
  }

  if (prev.description !== next.description) {
    rows.push({
      field: 'description',
      label: 'Description',
      before: prev.description,
      after: next.description,
    });
  }

  return rows;
}

/** Order-insensitive equality of two id lists. */
function sameIdSet(a: string[], b: string[]): boolean {
  if (a.length !== b.length) {
    return false;
  }
  const setB = new Set(b);
  return a.every((id) => setB.has(id));
}

/** Cap on emails shown per diff cell before collapsing the rest into a `+N more` tail. */
const MAX_USERS_SHOWN = 5;

/**
 * Formats a user-id list for a diff cell: each id resolved to its email
 * (falling back to the raw id for an unknown / deleted user), sorted for
 * a stable read, one per line, and truncated to {@link MAX_USERS_SHOWN}
 * with a `+N more` line. Newline-joined (the dialog renders the cell
 * `whitespace-pre-line`) so long emails sit one-per-line without trailing
 * commas wrapping awkwardly. Empty → '' renders as '—'.
 */
function formatUserList(ids: string[], userEmailById?: Map<string, string>): string {
  const labels = ids.map((id) => userEmailById?.get(id) ?? id).sort((a, b) => a.localeCompare(b));
  if (labels.length <= MAX_USERS_SHOWN) {
    return labels.join('\n');
  }
  return [...labels.slice(0, MAX_USERS_SHOWN), `+${labels.length - MAX_USERS_SHOWN} more`].join('\n');
}

const AUDIENCE_LABEL: Record<FeatureGateAudience, string> = {
  [FeatureGateAudience.ServerOnly]: 'Server only',
  [FeatureGateAudience.WebClient]: 'User app',
  [FeatureGateAudience.MopsWebClient]: 'Admin app',
  [FeatureGateAudience.AllClients]: 'User + admin apps',
};
